import React, { useCallback, useEffect, useRef, useState } from "react";
import PerspectiveCropEditor from "./PerspectiveCropEditor";
import CropAspectPicker, { aspectOptions, defaultAspectChoice, selectedAspect } from "./CropAspectPicker";

// Box image curation of one LaunchBox item, by launchbox_id alone so it works for
// consoles and games (/admin/cover-curation/:id in the API). Review the LaunchBox front
// (approve it or mark it as not a box), and replace any face with a perspective crop of
// one of the item's LaunchBox images or an uploaded file. Every write answers with the
// item's full state.
//
// A game keeps its 3D box by default (set it off in the Metadata modal), so it skips the
// front review and the top face; its side face is served as the spine.
//
// The LaunchBox image host refuses the production server, and the browser won't let the
// panel read an image from another site either. So for a crop the admin copies the
// image (right-click → Copy image) and pastes it into the editor, or drops a file; the
// pasted image goes up with the corners. Without one, the API downloads the source
// itself, which only works locally.

export const CURATION_STATUS_LABELS = {
  pending: "Unverified",
  approved: "Approved",
  curated: "Own images",
  not_cover: "Not a box",
  manual: "3D off · manual",
};

const FACES = [
  { key: "front", label: "Front", folders: ["art_front"] },
  { key: "back", label: "Back", folders: ["art_back"] },
  { key: "top", label: "Top", folders: ["art_front"] },
  { key: "side", label: "Side", folders: ["spine_card", "side_art", "art_front"] },
];
const GAME_FACES = [
  FACES[0],
  FACES[1],
  { ...FACES[3], label: "Spine" },
];

// What the app shows as a game's spine without a face of ours (SPINE_FOLDERS in the API).
function launchboxSpine(state) {
  for (const folder of ["spine_card", "side_art"]) {
    const match = state.sources.find((source) => source.folder === folder);
    if (match) return match.url;
  }
  return null;
}

function statusLabel(state) {
  return state.kind === "game" && state.status === "pending" ? "LaunchBox" : CURATION_STATUS_LABELS[state.status];
}

const ERROR_MESSAGES = {
  SOURCE_IMAGE_UNAVAILABLE: "The server couldn't download this image. Copy it (right-click → Copy image) and paste it here with ⌘V, then apply again.",
};

// The first image in a paste or a drop.
function imageFile(dataTransfer) {
  return [...(dataTransfer?.files || [])].find((file) => file.type.startsWith("image/")) || null;
}

const REVIEW_ACTIONS = [
  { status: "approved", label: "Approve front", title: "LaunchBox's front is a flat box front: 3D on" },
  { status: "not_cover", label: "Not a box", title: "Not a box front: render flat (3D off)" },
  { status: "pending", label: "Reset review", title: "Back to unverified; own images stay" },
];

// Where a face's crop starts: the source it was cut from, else its usual LaunchBox folder.
function defaultSource(face, state) {
  const current = state.faces[face.key];
  if (current?.source_url && state.sources.some((source) => source.url === current.source_url)) return current.source_url;
  for (const folder of face.folders) {
    const match = state.sources.find((source) => source.folder === folder);
    if (match) return match.url;
  }
  return state.sources[0]?.url ?? null;
}

function CoverCurationModal({ launchboxId, apiFetch, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cropping, setCropping] = useState(null); // { face, sourceUrl }
  const [pasted, setPasted] = useState(null); // { file, url } — the image the admin handed over
  // The crop in progress: its corners, the image's natural size, the proportion picked.
  const [cropPoints, setCropPoints] = useState(null);
  const [edgePoint, setEdgePoint] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [aspectChoice, setAspectChoice] = useState(null);
  const [customAspect, setCustomAspect] = useState({ width: 0, height: 0 });
  const fileInputRef = useRef(null);

  // Marking the side edge is asking for its estimate.
  const edgeMarked = Boolean(edgePoint);
  useEffect(() => {
    if (edgeMarked) setAspectChoice("estimated");
  }, [edgeMarked]);
  const uploadFaceRef = useRef(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && !busy) {
        if (cropping) closeCrop();
        else onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, cropping, busy]);

  // One pasted image at a time; its object URL is freed once it's replaced or dropped.
  useEffect(() => () => {
    if (pasted) URL.revokeObjectURL(pasted.url);
  }, [pasted]);

  const takeImage = useCallback((file) => {
    setPasted({ file, url: URL.createObjectURL(file) });
    setError(null);
  }, []);

  function selectSource(sourceUrl) {
    setCropping((current) => ({ ...current, sourceUrl }));
    setPasted(null);
    setImageSize(null);
  }

  // A face cropped before keeps the proportion it was saved with.
  function openCrop(face) {
    const saved = state.faces[face.key]?.aspect;
    setCropping({ face, sourceUrl: defaultSource(face, state) });
    setImageSize(null);
    setAspectChoice(saved?.source ?? null);
    setCustomAspect(saved?.source === "custom" ? { width: Math.round(saved.ratio * 1000), height: 1000 } : { width: 0, height: 0 });
  }

  function closeCrop() {
    setCropping(null);
    setPasted(null);
    setImageSize(null);
    setEdgePoint(null);
  }

  useEffect(() => {
    if (!cropping) return undefined;
    function handlePaste(e) {
      const file = imageFile(e.clipboardData);
      if (!file) return;
      e.preventDefault();
      takeImage(file);
    }
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [cropping, takeImage]);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/admin/cover-curation/${launchboxId}`)
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch((err) => {
        if (!cancelled && err.message !== "Forbidden") setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, launchboxId]);

  async function run(path, options) {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch(`/admin/cover-curation/${launchboxId}${path}`, options);
      setState(data);
      onChanged?.();
      return true;
    } catch (err) {
      if (err.message !== "Forbidden") setError(ERROR_MESSAGES[err.message] || err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  function setStatus(status) {
    run("/status", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  }

  async function applyCrop(points) {
    if (aspectChoice === "custom" && !aspect) {
      setError("Type the custom width and height first.");
      return;
    }
    const path = `/faces/${cropping.face.key}/crop`;
    const aspectFields = aspect ? { aspect: String(aspect.ratio), aspect_source: aspect.source } : {};
    const ok = pasted
      ? await run(`${path}?${new URLSearchParams({ points: JSON.stringify(points), source_url: cropping.sourceUrl, ...aspectFields })}`, {
        method: "POST",
        headers: { "Content-Type": pasted.file.type },
        body: pasted.file,
      })
      : await run(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_url: cropping.sourceUrl, points, ...(aspect ? { aspect: aspect.ratio, aspect_source: aspect.source } : {}) }),
      });
    if (ok) closeCrop();
  }

  function pickUpload(face) {
    uploadFaceRef.current = face;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  }

  function upload(e) {
    const file = e.target.files?.[0];
    if (!file || !uploadFaceRef.current) return;
    run(`/faces/${uploadFaceRef.current.key}`, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
  }

  function removeFace(face) {
    if (!window.confirm(`Remove the ${face.label.toLowerCase()} image? LaunchBox's goes back in its place.`)) return;
    run(`/faces/${face.key}`, { method: "DELETE" });
  }

  const cropFace = cropping?.face;
  const cropInitialPoints = cropFace && state?.faces[cropFace.key]?.source_url === cropping.sourceUrl
    ? state.faces[cropFace.key].points
    : null;
  const aspectChoices = cropFace && cropPoints
    ? aspectOptions({ face: cropFace.key, points: cropPoints, size: imageSize, sourceUrl: cropping.sourceUrl, state, edgePoint })
    : [];
  // The picked option while it's available (an estimate can drop out as corners move).
  const activeAspectChoice = aspectChoices.some((option) => option.key === aspectChoice && (option.ratio != null || option.key === "custom"))
    ? aspectChoice
    : defaultAspectChoice(aspectChoices);
  const aspect = selectedAspect(aspectChoices, activeAspectChoice, customAspect);

  return (
    <div className="admin-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="admin-modal admin-modal--curation admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close" disabled={busy}>
          ×
        </button>
        <h3 className="admin-modal-title">
          {cropFace ? `Crop ${cropFace.label.toLowerCase()}` : "Cover curation"}
        </h3>

        {!state && !error && <p className="admin-status">Loading…</p>}
        {state && (
          <div className="admin-source-meta">
            {state.name} · {state.platform_name || "Unknown platform"} · launchbox_id {state.launchbox_id}
            <span className={`admin-curation-status admin-curation-status--${state.status}`}>
              {statusLabel(state)}
            </span>
          </div>
        )}
        {error && <p className="admin-status admin-error">{error}</p>}

        {state && cropFace && (
          <>
            {state.sources.length > 1 && (
              <div className="admin-curation-sources" role="radiogroup" aria-label="Source image">
                {state.sources.map((source) => (
                  <button
                    type="button"
                    key={source.url}
                    role="radio"
                    aria-checked={cropping.sourceUrl === source.url}
                    className={`admin-curation-source ${cropping.sourceUrl === source.url ? "admin-curation-source--active" : ""}`}
                    onClick={() => selectSource(source.url)}
                    disabled={busy}
                  >
                    <img src={source.url} alt={source.folder} />
                    <span>{source.folder}</span>
                  </button>
                ))}
              </div>
            )}
            <div className={`admin-crop-paste ${pasted ? "admin-crop-paste--ready" : ""}`}>
              {pasted
                ? (
                  <>
                    <span>Using the pasted image.</span>
                    <button type="button" className="admin-signout-btn" onClick={() => setPasted(null)} disabled={busy}>
                      Discard
                    </button>
                  </>
                )
                : <span>Right-click the image → Copy image, then press ⌘V here (or drop an image file). Production needs it: the server can't download LaunchBox images.</span>}
            </div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const file = imageFile(e.dataTransfer);
                if (!file) return;
                e.preventDefault();
                takeImage(file);
              }}
            >
              {/* Keyed by the source only: pasting the same image keeps the corners. */}
              <PerspectiveCropEditor
                key={cropping.sourceUrl}
                src={pasted?.url || cropping.sourceUrl}
                initialPoints={cropInitialPoints}
                busy={busy}
                onApply={applyCrop}
                onCancel={closeCrop}
                onPointsChange={setCropPoints}
                onImageLoad={setImageSize}
                onEdgePointChange={setEdgePoint}
              >
                <CropAspectPicker
                  options={aspectChoices}
                  choice={activeAspectChoice}
                  onChoice={setAspectChoice}
                  custom={customAspect}
                  onCustom={setCustomAspect}
                  busy={busy}
                />
              </PerspectiveCropEditor>
            </div>
          </>
        )}

        {state && !cropFace && (
          <>
            <div className="admin-curation-review">
              <div className="admin-curation-review-text">
                <p className="admin-source-meta">
                  {state.served.disable_3d_reason
                    ? `The app renders this cover flat (${state.served.disable_3d_reason}).`
                    : "The app renders this cover as a 3D box."}
                </p>
                {state.kind === "game" && (
                  <p className="admin-source-meta">Turn the 3D box off in this game's Metadata.</p>
                )}
                {state.status === "pending" && state.approved_front_url && (
                  <p className="admin-source-meta">LaunchBox changed the front since it was approved.</p>
                )}
              </div>
              {state.kind !== "game" && (
                <div className="admin-row-actions admin-row-actions--gap">
                  {REVIEW_ACTIONS.map((action) => (
                    <button
                      type="button"
                      key={action.status}
                      className={`admin-signout-btn ${state.status === action.status ? "admin-curation-action--active" : ""}`}
                      title={action.title}
                      onClick={() => setStatus(action.status)}
                      disabled={busy || (action.status === "approved" && !state.original.front_url)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="admin-curation-faces">
              {(state.kind === "game" ? GAME_FACES : FACES).map((face) => {
                const own = state.faces[face.key];
                const launchbox = face.key === "front" ? state.original.front_url
                  : face.key === "back" ? state.original.back_url
                  : face.key === "side" && state.kind === "game" ? launchboxSpine(state)
                  : null;
                const src = own?.url || launchbox;
                return (
                  <div className="admin-curation-face" key={face.key}>
                    <div className="admin-curation-face-head">
                      <strong>{face.label}</strong>
                      <span className="admin-curation-origin">{own ? (own.points ? "cropped" : "uploaded") : launchbox ? "LaunchBox" : "none"}</span>
                    </div>
                    <div className="admin-curation-face-image">
                      {src ? <img src={src} alt={`${face.label} face`} /> : <span>No image</span>}
                    </div>
                    {own && (
                      <div className="admin-curation-size">
                        {own.width} × {own.height}
                        {own.aspect && ` · ${own.aspect.ratio.toFixed(3)} ${own.aspect.source}`}
                        {own.points && own.aspect === undefined && <span className="admin-error"> · no aspect, crop again</span>}
                      </div>
                    )}
                    <div className="admin-row-actions admin-row-actions--gap">
                      <button
                        type="button"
                        className="admin-signout-btn"
                        onClick={() => openCrop(face)}
                        disabled={busy || state.sources.length === 0}
                      >
                        Crop
                      </button>
                      <button type="button" className="admin-signout-btn" onClick={() => pickUpload(face)} disabled={busy}>
                        Upload
                      </button>
                      {own && (
                        <button type="button" className="admin-signout-btn admin-delete-btn" onClick={() => removeFace(face)} disabled={busy}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {busy && <p className="admin-status">Saving…</p>}
          </>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={upload} />
      </div>
    </div>
  );
}

export default CoverCurationModal;
